import React, { useEffect, useState } from 'react';
import {
  collection,
  doc,
  getDoc,
  getDocs,
  updateDoc,
  deleteDoc,
  serverTimestamp,
  setDoc,
  onSnapshot,
  addDoc,
  query,
  orderBy
} from 'firebase/firestore';
import { db } from '../firebase';
import { PlusIcon } from '../components/SvgIcons';
import './AdminTournamentPage.css';
import { useNavigate } from 'react-router-dom';
import { getAuth } from 'firebase/auth';
import { toast } from 'react-toastify';
import { 
  generateSwissPairings, 
  generateRoundRobinPairings, 
  generateTopCutPairings,
  getTournamentStandings,
  computeRoundRobinStandings,
  getNextRound
} from '../utils/tournamentPairingUtils';

const AdminTournamentPage = () => {
  const [tournaments, setTournaments] = useState([]);
  const [selectedTournamentId, setSelectedTournamentId] = useState('');
  const [participants, setParticipants] = useState([]);
  const [tournamentInfo, setTournamentInfo] = useState(null);
  const [pairings, setPairings] = useState([]);
  const [selectedPlayerA, setSelectedPlayerA] = useState('');
  const [selectedPlayerB, setSelectedPlayerB] = useState('');
  const [pairingRound, setPairingRound] = useState('');
  const [editedStatuses, setEditedStatuses] = useState({});
  const [editedNotes, setEditedNotes] = useState({});
  const [editedRounds, setEditedRounds] = useState({});
  const [selectedRoundFilter, setSelectedRoundFilter] = useState('');
  const [editedResults, setEditedResults] = useState({});
  const [editedAdminNotes, setEditedAdminNotes] = useState({});
  const [isAdmin, setIsAdmin] = useState(false);
  const allDivisions = ['BlueObelisk', 'YellowRa', 'RedSlifer', 'Unranked', 'Everyone'];
  const navigate = useNavigate();
  const auth = getAuth();
  const user = auth.currentUser;
  const [removeModalParticipant, setRemoveModalParticipant] = useState(null);
  const [creatingTournament, setCreatingTournament] = useState(false);
  const [copyingParticipants, setCopyingParticipants] = useState(false);
  const [savingPairing, setSavingPairing] = useState({}); // { [pairingId]: boolean }
  const [deletingPairing, setDeletingPairing] = useState({}); // { [pairingId]: boolean }
  const [removingParticipant, setRemovingParticipant] = useState(false);
  const [endingTournament, setEndingTournament] = useState(false);
  const [generatingPairings, setGeneratingPairings] = useState(false);
  
  // Discussion modal state
  const [discussionModal, setDiscussionModal] = useState(null); // { pairingId, playerAName, playerBName }
  const [discussionMessages, setDiscussionMessages] = useState([]);
  const [discussionInput, setDiscussionInput] = useState('');
  const [sendingMessage, setSendingMessage] = useState(false);
  const [deletingMessages, setDeletingMessages] = useState(false);
  const [manageResultUser, setManageResultUser] = useState(null);
  const [resultUpdateLog, setResultUpdateLog] = useState(() => {
    const stored = localStorage.getItem('resultUpdateLog');
    return stored ? JSON.parse(stored) : [];
  });
  const [showDeckModal, setShowDeckModal] = useState(false);
  const [tournamentDecks, setTournamentDecks] = useState([]);
  const [loadingDecks, setLoadingDecks] = useState(false);

  const [statIncrements, setStatIncrements] = useState({
    matches: 0,
    wins: 0,
    losses: 0,
    champion: 0
  });


  const formatTournamentId = (name) => {
    return name
      .toLowerCase()
      .trim()
      .replace(/[^a-z0-9_-]+/g, '-')     // Replace invalid chars with -
      .replace(/-+/g, '-')                // Collapse multiple dashes
      .replace(/^-|-$/g, '')              // Remove leading/trailing dashes
      || 'untitled';                      // Fallback if empty
  };

  const getPseudoById = (uid) => {
    const player = participants.find(p => p.id === uid);
    return player ? player.pseudo : uid;
  };

  const fetchTournamentDecks = async (tournamentId) => {
    setLoadingDecks(true);
    // Clear previous results immediately
    setTournamentDecks([]);
    
    try {
      const decks = [];
      
      console.log('🔍 Fetching decks for tournament:', tournamentId);
      
      // Get all participants for this tournament
      const participantsRef = collection(db, 'tournament-v2', 'upcoming', 'list', tournamentId, 'participants');
      const participantsSnapshot = await getDocs(participantsRef);
      
      console.log('📋 Found participants:', participantsSnapshot.docs.length);
      
      for (const participantDoc of participantsSnapshot.docs) {
        const userId = participantDoc.id;
        const participantData = participantDoc.data();
        
        console.log('👤 Checking participant:', userId, participantData.pseudo);
        
        // Get user's tournament deck from the new location
        const tournamentDeckRef = doc(db, 'tournament-v2', 'upcoming', 'list', tournamentId, 'participants', userId, 'submittedDeck', 'current');
        const tournamentDeckDoc = await getDoc(tournamentDeckRef);
        
        if (tournamentDeckDoc.exists()) {
          const deckData = tournamentDeckDoc.data();
          decks.push({
            userId,
            username: participantData.pseudo || 'Unknown',
            deckName: deckData.name,
            main: deckData.main || [],
            extra: deckData.extra || [],
            side: deckData.side || [],
            submittedAt: deckData.submittedAt,
            updatedAt: deckData.updatedAt
          });
        }
      }
      
      console.log('🎯 Final decks array:', decks);
      setTournamentDecks(decks);
    } catch (error) {
      console.error('Error fetching tournament decks:', error);
      toast.error('Failed to fetch tournament decks');
      // Clear results on error too
      setTournamentDecks([]);
    } finally {
      setLoadingDecks(false);
    }
  };

  const [cardDatabase, setCardDatabase] = useState(null);

  const fetchUserDeck = async (tournamentId, userId, username) => {
    setLoadingDecks(true);
    setTournamentDecks([]);
    
    try {
      console.log('🔍 Fetching deck for user:', userId, 'in tournament:', tournamentId);
      
      // Get user's tournament deck from the new location
      const tournamentDeckRef = doc(db, 'tournament-v2', 'upcoming', 'list', tournamentId, 'participants', userId, 'submittedDeck', 'current');
      const tournamentDeckDoc = await getDoc(tournamentDeckRef);
      
      if (tournamentDeckDoc.exists()) {
        const deckData = tournamentDeckDoc.data();
        console.log('✅ Found deck for user:', userId, 'Deck name:', deckData.name);
        
        // Fetch card database if not already loaded
        let cardDB = cardDatabase;
        if (!cardDB) {
          try {
            const res = await fetch('https://db.ygoprodeck.com/api/v7/cardinfo.php');
            const data = await res.json();
            cardDB = data.data || [];
            cardDB = Array.from(new Map(cardDB.map(c => [String(c.id), c])).values());
            setCardDatabase(cardDB);
          } catch (err) {
            console.error('Failed to fetch card database:', err);
            toast.error('Failed to load card images');
            return;
          }
        }
        
        // Resolve card IDs to full card objects
        const resolveCards = (idList) => idList.map(id => {
          const card = cardDB.find(c => String(c.id) === String(id)) ||
            cardDB.find(c => Array.isArray(c.misc_info?.alias) && c.misc_info.alias.includes(Number(id))) ||
            cardDB.find(c => c.card_images?.some(img => String(img.id) === String(id))) ||
            {
              id,
              name: `Unknown (${id})`,
              card_images: [{ image_url: 'https://via.placeholder.com/80x115?text=Unknown' }]
            };
          return card;
        });
        
        setTournamentDecks([{
          userId,
          username: username || 'Unknown',
          deckName: deckData.name,
          main: resolveCards(deckData.main || []),
          extra: resolveCards(deckData.extra || []),
          side: resolveCards(deckData.side || []),
          submittedAt: deckData.submittedAt,
          updatedAt: deckData.updatedAt
        }]);
      } else {
        console.log('❌ No deck found for user:', userId);
        setTournamentDecks([]);
      }
    } catch (error) {
      console.error('Error fetching user deck:', error);
      toast.error('Failed to fetch user deck');
      setTournamentDecks([]);
    } finally {
      setLoadingDecks(false);
    }
  };

  // Load all tournaments in list
  useEffect(() => {
    const fetchTournaments = async () => {
      try {
        const snapshot = await getDocs(collection(db, 'tournament-v2', 'upcoming', 'list'));
        const list = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        setTournaments(list);
      } catch (error) {
        // console.error('Error loading tournaments:', error);
      }
    };
    fetchTournaments();
  }, []);

  // Load selected tournament data
  useEffect(() => {
    if (!selectedTournamentId) return;

    const fetchData = async () => {
      try {
        const tourRef = doc(db, 'tournament-v2', 'upcoming', 'list', selectedTournamentId);
        const tourDoc = await getDoc(tourRef);
        if (tourDoc.exists()) {
          setTournamentInfo(tourDoc.data());
        } else {
          setTournamentInfo(null);
        }

        const participantsSnap = await getDocs(collection(db, 'tournament-v2', 'upcoming', 'list', selectedTournamentId, 'participants'));
        const participantList = participantsSnap.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        setParticipants(participantList);
      } catch (error) {
        // console.error('Error fetching tournament data:', error);
      }
    };

    fetchData();

    const unsubscribe = onSnapshot(
      collection(db, 'tournament-v2', 'upcoming', 'list', selectedTournamentId, 'TournamentSchedule'),
      (snapshot) => {
        const pairingList = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        setPairings(pairingList);
      }
    );

    return () => unsubscribe();
  }, [selectedTournamentId]);

  useEffect(() => {
    if (!user) return;
    // Try to read /admins/{uid}
    getDoc(doc(db, 'admins', user.uid)).then((docSnap) => {
      setIsAdmin(docSnap.exists());
    });
  }, [user]);

  // HANDLERS
  const handleResultChange = (userId, newResult) => {
    setParticipants((prev) =>
      prev.map((p) =>
        p.id === userId ? { ...p, result: newResult } : p
      )
    );
  };

  const handleCreateTournament = async () => {
    setCreatingTournament(true);
    const titleInput = prompt('Enter the tournament title:');
    
    // Handle case where user cancels the prompt (returns null)
    if (titleInput === null) {
      setCreatingTournament(false);
      return; // User cancelled, just return without error
    }
    
    const title = titleInput.trim();

    if (!title) {
      toast.error('❌ Tournament title is required.');
      setCreatingTournament(false);
      return;
    }

    const tournamentId = formatTournamentId(title);

    try {
      // Check if ID already exists
      const existing = await getDoc(doc(db, 'tournament-v2', 'upcoming', 'list', tournamentId));
      if (existing.exists()) {
        toast.error('❌ A tournament with that ID already exists. Please choose another title.');
        setCreatingTournament(false);
        return;
      }

      await setDoc(doc(db, 'tournament-v2', 'upcoming', 'list', tournamentId), {
        title,
        date: '',
        bannerUrl: '',
        fees: '',
        format: '',
        link: '',
        platform: '',
        started: false,
        description: '',
        createdAt: serverTimestamp(),
      });

      toast.success(`✅ New tournament created with ID: ${tournamentId}`);
      setSelectedTournamentId(tournamentId);

      // Refresh tournaments list
      const snapshot = await getDocs(collection(db, 'tournament-v2', 'upcoming', 'list'));
      const list = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setTournaments(list);

    } catch (error) {
      // console.error(error);
      toast.error('❌ Failed to create tournament.');
    } finally {
      setCreatingTournament(false);
    }
  };



  const handleCopyParticipants = () => {
    setCopyingParticipants(true);
    if (!participants.length) {
      toast.error('❌ No participants to copy.');
      setCopyingParticipants(false);
      return;
    }

    const participantList = participants.map(p => p.pseudo).join('\n');
    navigator.clipboard.writeText(participantList)
      .then(() => {
        toast.success('✅ Participant list copied to clipboard!');
      })
      .catch((err) => {
        // console.error(err);
        toast.error('❌ Failed to copy participants.');
      })
      .finally(() => setCopyingParticipants(false));
  };

  const handleSaveAll = async () => {
    for (const p of participants) {
      const partRef = doc(db, 'tournament-v2', 'upcoming', 'list', selectedTournamentId, 'participants', p.id);
      await updateDoc(partRef, {
        result: p.result,
        placement: p.result // ✅ update placement as well
      });

      if (tournamentInfo) {
        const userTourRef = doc(db, 'users', p.id, 'tournaments', selectedTournamentId);
        await updateDoc(userTourRef, {
          result: p.result,
          placement: p.result // ✅ update placement here too
        });
      }
    }

    toast.success('✅ All results & placements saved!');
  };


  const handleRemoveParticipant = async (participant, removeEverything) => {
    setRemovingParticipant(true);
    if (!window.confirm(`❌ Are you sure you want to remove ${participant.pseudo}?`)) {
      setRemovingParticipant(false);
      return;
    }

    try {
      // 1️⃣ Always remove from participants collection of this tournament
      await deleteDoc(doc(db, 'tournament-v2', 'upcoming', 'list', selectedTournamentId, 'participants', participant.id));

      // 2️⃣ Remove all TournamentSchedule pairings containing them
      const pairingsSnapshot = await getDocs(collection(db, 'tournament-v2', 'upcoming', 'list', selectedTournamentId, 'TournamentSchedule'));
      const scheduleDeletes = [];
      pairingsSnapshot.forEach((docSnap) => {
        const data = docSnap.data();
        if (data.playerIds && data.playerIds.includes(participant.id)) {
          scheduleDeletes.push(deleteDoc(docSnap.ref));
        }
      });
      await Promise.all(scheduleDeletes);

      // 3️⃣ Remove all matches in users/{id}/matches that belong to this tournament
      const userMatchesSnap = await getDocs(collection(db, 'users', participant.id, 'matches'));
      const matchDeletes = [];
      userMatchesSnap.forEach((matchDoc) => {
        if (matchDoc.data()?.tournamentId === selectedTournamentId) {
          matchDeletes.push(deleteDoc(matchDoc.ref));
        }
      });
      await Promise.all(matchDeletes);

      // 4️⃣ Only in Remove from Everything: also remove users/{id}/tournaments/{tournamentId}
      if (removeEverything) {
        await deleteDoc(doc(db, 'users', participant.id, 'tournaments', selectedTournamentId));
      }

      setParticipants((prev) => prev.filter((p) => p.id !== participant.id));
      setRemoveModalParticipant(null);
      toast(`✅ ${participant.pseudo} has been removed${removeEverything ? ' from everything' : ' from tournament'}.`);
    } catch (error) {
      // console.error(error);
      toast.error('❌ Failed to remove participant.');
    } finally {
      setRemovingParticipant(false);
    }
  };


  const handleAddPairing = async () => {
    if (!selectedPlayerA || !selectedPlayerB) {
      toast.error('❌ Please select both players.');
      return;
    }
    if (selectedPlayerA === selectedPlayerB) {
      toast.error('❌ Players must be different.');
      return;
    }
    if (!pairingRound.trim()) {
      toast.error('❌ Please enter a round number.');
      return;
    }

    const playerA = participants.find(p => p.id === selectedPlayerA);
    const playerB = participants.find(p => p.id === selectedPlayerB);

    if (!playerA || !playerB) {
      toast.error('❌ Invalid player selection.');
      return;
    }

    // Defensive clean
    const playerAId = playerA.id.trim();
    const playerBId = playerB.id.trim();

    if (!playerAId || !playerBId) {
      toast.error('❌ Player IDs are invalid.');
      return;
    }

    try {
      // Create the master match in TournamentSchedule
      const newMatchRef = doc(collection(db, 'tournament-v2', 'upcoming', 'list', selectedTournamentId, 'TournamentSchedule'));
      await setDoc(newMatchRef, {
        playerAId: playerAId,
        playerAName: playerA.pseudo,
        playerBId: playerBId,
        playerBName: playerB.pseudo,
        playerIds: [playerAId, playerBId],
        status: 'open',
        tournamentId: selectedTournamentId,
        round: pairingRound.trim(),
        adminNote: '',
        playerANote: '',
        playerBNote: '',
        confirmedBy: [],
        createdAt: serverTimestamp()
      });

      // Fan out to both players
      const matchDataForUser = {
        matchId: newMatchRef.id,
        tournamentId: selectedTournamentId,
        playerAId: playerAId,
        playerAName: playerA.pseudo,
        playerBId: playerBId,
        playerBName: playerB.pseudo,
        status: 'open',
        round: pairingRound.trim(),
        adminNote: '',
        playerANote: '',
        playerBNote: '',
        confirmedBy: [],
        createdAt: serverTimestamp()
      };
      // console.log('✅ Fan-out to Player A:', playerAId);
      // console.log('✅ Fan-out to Player B:', playerBId);

      await setDoc(doc(db, 'users', playerAId, 'matches', newMatchRef.id), matchDataForUser);
      await setDoc(doc(db, 'users', playerBId, 'matches', newMatchRef.id), matchDataForUser);


      setSelectedPlayerA('');
      setSelectedPlayerB('');
      setPairingRound('');
      toast.success('✅ Pairing created!');
    } catch (error) {
      // console.error(error);
      toast.error('❌ Failed to create pairing.');
    }
  };



  const handleDeletePairing = async (matchId) => {
    setDeletingPairing(prev => ({ ...prev, [matchId]: true }));
    try {
      // Find the pairing in local state
      const pairing = pairings.find(p => p.id === matchId);
      if (!pairing) {
        toast.error('❌ Could not find pairing data.');
        return;
      }

      // Delete master match in TournamentSchedule
      await deleteDoc(
        doc(db, 'tournament-v2', 'upcoming', 'list', selectedTournamentId, 'TournamentSchedule', matchId)
      );

      // Delete fan-out copies in both players
      await Promise.all([
        deleteDoc(doc(db, 'users', pairing.playerAId, 'matches', matchId)),
        deleteDoc(doc(db, 'users', pairing.playerBId, 'matches', matchId))
      ]);

      setPairings((prev) => prev.filter((m) => m.id !== matchId));
      toast.success('✅ Pairing deleted from schedule and players!');
    } catch (error) {
      // console.error(error);
      toast.error('❌ Failed to delete pairing.');
    } finally {
      setDeletingPairing(prev => ({ ...prev, [matchId]: false }));
    }
  };


  const handleSavePairing = async (pairing) => {
    setSavingPairing(prev => ({ ...prev, [pairing.id]: true }));
    try {
      const updatedData = {
        status: pairing.status,
        round: pairing.round,
        adminNote: pairing.adminNote,
        playerANote: pairing.playerANote,
        playerBNote: pairing.playerBNote
      };

      // Update master match in TournamentSchedule
      await updateDoc(
        doc(db, 'tournament-v2', 'upcoming', 'list', selectedTournamentId, 'TournamentSchedule', pairing.id),
        updatedData
      );

      // Fan out to Player A
      await updateDoc(
        doc(db, 'users', pairing.playerAId, 'matches', pairing.id),
        updatedData
      );

      // Fan out to Player B
      await updateDoc(
        doc(db, 'users', pairing.playerBId, 'matches', pairing.id),
        updatedData
      );

      toast.success('✅ Pairing updated and synced to players!');
    } catch (error) {
      // console.error(error);
      toast.error('❌ Failed to update pairing.');
    } finally {
      setSavingPairing(prev => ({ ...prev, [pairing.id]: false }));
    }
  };



  const handleSaveTournamentInfo = async () => {
    try {
      // Prepare the update object
      const updateData = { ...tournamentInfo };
      
      // Auto-set phase when format is configured and tournament is started
      if (tournamentInfo.formatType && tournamentInfo.started && !tournamentInfo.phase) {
        if (tournamentInfo.formatType === 'swiss-topcut') {
          updateData.phase = 'swiss';
          updateData.currentRound = updateData.currentRound || 0;
        } else if (tournamentInfo.formatType === 'roundrobin-topcut') {
          updateData.phase = 'roundrobin';
          updateData.currentRound = updateData.currentRound || 0;
        }
      }
      
      // If tournament is not started, reset phase
      if (!tournamentInfo.started && tournamentInfo.phase) {
        updateData.phase = 'not-started';
        updateData.currentRound = 0;
      }
      
      await updateDoc(doc(db, 'tournament-v2', 'upcoming', 'list', selectedTournamentId), updateData);
      
      // Update local state
      setTournamentInfo(updateData);
      
      toast.success('✅ Tournament info updated!');
    } catch (error) {
      // console.error(error);
      toast.error('❌ Failed to save tournament info.');
    }
  };

  // Discussion functions
  const openDiscussion = (pairing) => {
    setDiscussionModal({
      pairingId: pairing.id,
      playerAName: getPseudoById(pairing.playerAId),
      playerBName: getPseudoById(pairing.playerBId)
    });
    setDiscussionMessages([]);
    setDiscussionInput('');
    loadDiscussionMessages(pairing.id);
  };

  const closeDiscussion = () => {
    setDiscussionModal(null);
    setDiscussionMessages([]);
    setDiscussionInput('');
  };

  const loadDiscussionMessages = async (pairingId) => {
    try {
      const chatRef = collection(
        db, 'tournament-v2', 'upcoming', 'list', selectedTournamentId,
        'TournamentSchedule', pairingId, 'chat'
      );
      
      const q = query(chatRef, orderBy('timestamp', 'asc'));
      const snapshot = await getDocs(q);
      const messages = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      setDiscussionMessages(messages);
    } catch (error) {
      console.error('Error loading discussion messages:', error);
      toast.error('Failed to load discussion messages');
    }
  };

  const sendDiscussionMessage = async () => {
    if (!discussionInput.trim() || !discussionModal || sendingMessage) return;

    setSendingMessage(true);
    try {
      const chatRef = collection(
        db, 'tournament-v2', 'upcoming', 'list', selectedTournamentId,
        'TournamentSchedule', discussionModal.pairingId, 'chat'
      );
      
      await addDoc(chatRef, {
        text: discussionInput.trim(),
        senderId: user.uid,
        senderName: 'Admin',
        timestamp: serverTimestamp(),
        isAdminMessage: true
      });
      
      setDiscussionInput('');
      // Reload messages to show the new one
      loadDiscussionMessages(discussionModal.pairingId);
      toast.success('Message sent!');
    } catch (error) {
      console.error('Error sending message:', error);
      toast.error('Failed to send message');
    } finally {
      setSendingMessage(false);
    }
  };

  const deleteAllMessages = async () => {
    if (!discussionModal || deletingMessages) return;

    const confirmed = window.confirm('Are you sure you want to delete ALL messages in this discussion? This action cannot be undone.');
    if (!confirmed) return;

    setDeletingMessages(true);
    try {
      const chatRef = collection(
        db, 'tournament-v2', 'upcoming', 'list', selectedTournamentId,
        'TournamentSchedule', discussionModal.pairingId, 'chat'
      );
      
      // Get all messages first
      const messagesSnapshot = await getDocs(chatRef);
      
      // Delete each message
      const deletePromises = messagesSnapshot.docs.map(doc => deleteDoc(doc.ref));
      await Promise.all(deletePromises);
      
      // Clear local state
      setDiscussionMessages([]);
      toast.success('All messages deleted successfully!');
    } catch (error) {
      console.error('Error deleting messages:', error);
      toast.error('Failed to delete messages');
    } finally {
      setDeletingMessages(false);
    }
  };

  // Tournament Format Handlers
  const handleGenerateNextRound = async () => {
    if (!selectedTournamentId || !tournamentInfo) {
      toast.error('Please select a tournament first');
      return;
    }

    if (!tournamentInfo.formatType) {
      toast.error('Please configure tournament format first');
      return;
    }

    setGeneratingPairings(true);

    try {
      const currentRound = tournamentInfo.currentRound || 0;
      const phase = tournamentInfo.phase || 'swiss';
      let newPairings = [];

      if (tournamentInfo.formatType === 'swiss-topcut') {
        if (phase === 'swiss') {
          const nextRoundNum = currentRound + 1;
          
          if (nextRoundNum > (tournamentInfo.swissRounds || 0)) {
            toast.error('All Swiss rounds completed. Generate Top Cut instead.');
            setGeneratingPairings(false);
            return;
          }

          newPairings = await generateSwissPairings(
            participants, 
            pairings, 
            nextRoundNum,
            db
          );

          // Update tournament info
          await updateDoc(doc(db, 'tournament-v2', 'upcoming', 'list', selectedTournamentId), {
            currentRound: nextRoundNum
          });

          toast.success(`Generated Round ${nextRoundNum} pairings!`);
        }
      } else if (tournamentInfo.formatType === 'roundrobin-topcut') {
        if (phase === 'roundrobin') {
          if (currentRound > 0 && pairings.length > 0) {
            toast.error('Round Robin pairings already generated');
            setGeneratingPairings(false);
            return;
          }

          const numberOfGroups = tournamentInfo.roundRobinGroups || 1;
          const rrType = tournamentInfo.roundRobinType || 'single';
          newPairings = generateRoundRobinPairings(participants, numberOfGroups, {
            configOverrides: {
              roundRobin: { type: rrType }
            }
          });

          const maxRound = newPairings.reduce((max, p) => Math.max(max, p.roundNumber || 1), 1);

          await updateDoc(doc(db, 'tournament-v2', 'upcoming', 'list', selectedTournamentId), {
            currentRound: maxRound,
            phase: 'roundrobin'
          });

          if (numberOfGroups > 1) {
            toast.success(`Generated Round Robin pairings for ${numberOfGroups} groups (${maxRound} rounds each).`);
          } else {
            toast.success(`Generated all Round Robin pairings (${maxRound} rounds).`);
          }
        }
      }

      // Save new pairings to Firestore
      for (const pairing of newPairings) {
        const newPairingRef = doc(
          collection(db, 'tournament-v2', 'upcoming', 'list', selectedTournamentId, 'TournamentSchedule')
        );

        await setDoc(newPairingRef, {
          ...pairing,
          tournamentId: selectedTournamentId,
          playerAName: pairing.playerAName || getPseudoById(pairing.playerAId),
          playerBName: pairing.playerBName || getPseudoById(pairing.playerBId),
          playerIds: pairing.playerIds || [pairing.playerAId, pairing.playerBId].filter(Boolean),
          group: pairing.group,
          roundNumber: pairing.roundNumber,
          stage: pairing.stage || tournamentInfo.phase || 'round_robin',
          status: pairing.result?.outcome === 'BYE' ? 'completed' : pairing.status || 'pending',
          result: pairing.result || null,
          reports: pairing.reports || {},
          adminNote: '',
          playerANote: '',
          playerBNote: '',
          createdAt: serverTimestamp()
        });
      }

      toast.success('Pairings saved to tournament!');
    } catch (error) {
      console.error('Error generating pairings:', error);
      toast.error(`Failed to generate pairings: ${error.message}`);
    } finally {
      setGeneratingPairings(false);
    }
  };

  const handleGenerateTopCut = async () => {
    if (!selectedTournamentId || !tournamentInfo) {
      toast.error('Please select a tournament first');
      return;
    }

    if (!tournamentInfo.topCutSize) {
      toast.error('Please configure Top Cut size first');
      return;
    }

    const topCutSize = Number(tournamentInfo.topCutSize);
    
    if (participants.length < topCutSize) {
      toast.error(`Not enough participants for Top ${topCutSize}`);
      return;
    }

    if (!window.confirm(`Generate Top ${topCutSize} bracket? This will create elimination matches based on current standings.`)) {
      return;
    }

    setGeneratingPairings(true);

    try {
      let topCutPairings = [];

      if (tournamentInfo.formatType === 'roundrobin-topcut') {
        const rrStandings = computeRoundRobinStandings(participants, pairings);
        const top = rrStandings.slice(0, topCutSize);

        const roundName = topCutSize === 2 ? 'Finals' : 
                          topCutSize === 4 ? 'Top 4 - Semifinals' :
                          topCutSize === 8 ? 'Top 8 - Quarterfinals' :
                          `Top ${topCutSize}`;

        for (let i = 0; i < topCutSize / 2; i++) {
          topCutPairings.push({
            playerAId: top[i]?.participantId,
            playerBId: top[topCutSize - 1 - i]?.participantId,
            round: roundName,
            stage: 'top_cut',
            seedA: top[i]?.seed,
            seedB: top[topCutSize - 1 - i]?.seed
          });
        }
      } else {
        topCutPairings = generateTopCutPairings(participants, pairings, topCutSize);
      }

      // Save top cut pairings
      for (const pairing of topCutPairings.filter(p => p.playerAId && p.playerBId)) {
        const newPairingRef = doc(
          collection(db, 'tournament-v2', 'upcoming', 'list', selectedTournamentId, 'TournamentSchedule')
        );
        
        await setDoc(newPairingRef, {
          ...pairing,
          tournamentId: selectedTournamentId,
          playerAName: getPseudoById(pairing.playerAId),
          playerBName: getPseudoById(pairing.playerBId),
          playerIds: [pairing.playerAId, pairing.playerBId],
          stage: 'top_cut',
          status: 'pending',
          roundNumber: pairing.roundNumber || 1,
          reports: pairing.reports || {},
          adminNote: '',
          playerANote: '',
          playerBNote: '',
          createdAt: serverTimestamp()
        });
      }

      // Update tournament phase
      await updateDoc(doc(db, 'tournament-v2', 'upcoming', 'list', selectedTournamentId), {
        phase: 'topcut',
        currentRound: 1
      });

      toast.success(`Generated Top ${topCutSize} bracket!`);
    } catch (error) {
      console.error('Error generating top cut:', error);
      toast.error(`Failed to generate top cut: ${error.message}`);
    } finally {
      setGeneratingPairings(false);
    }
  };

  const handleViewStandings = () => {
    if (!participants.length || !pairings.length) {
      toast.info('No standings available yet');
      return;
    }

    const standings = tournamentInfo?.formatType === 'roundrobin-topcut'
      ? computeRoundRobinStandings(participants, pairings)
      : getTournamentStandings(participants, pairings);
    
    let message = 'Current Standings:\n\n';
    standings.forEach((player, index) => {
      const pseudo = player.pseudo || player.name || getPseudoById(player.id || player.participantId);
      const wins = player.wins ?? player.matchWins ?? 0;
      const losses = player.losses ?? player.matchLosses ?? 0;
      const draws = player.draws ?? player.matchTies ?? 0;
      const points = player.matchPoints ?? player.points ?? 0;
      message += `${index + 1}. ${pseudo} - ${wins}W/${losses}L/${draws}D (${points} pts)\n`;
    });

    alert(message);
  };

  const formatTimestamp = (timestamp) => {
    if (!timestamp?.seconds) return '';
    const date = new Date(timestamp.seconds * 1000);
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const handleEndTournament = async () => {
    setEndingTournament(true);
    if (!window.confirm("⚠️ Are you sure you want to END this tournament? This will archive it and clean all participant matches.")) {
      setEndingTournament(false);
      return;
    }

    try {
      const tournamentPath = `tournament-v2/upcoming/list/${selectedTournamentId}`;
      console.log('🔍 Looking for tournament at path:', tournamentPath);

      // 1️⃣ Copy tournament data to TournamentArchive
      const tourRef = doc(db, tournamentPath);
      const tourSnap = await getDoc(tourRef);

      if (!tourSnap.exists()) {
        console.error('❌ Tournament not found at path:', tournamentPath);
        toast.error('❌ Tournament not found!');
        return;
      }

      console.log('✅ Tournament found:', tourSnap.data());

      const tournamentData = tourSnap.data();
      // Use the same structure as the source: tournament-v2/archived/list/{tournamentId}
      await setDoc(doc(db, 'tournament-v2', 'archived', 'list', selectedTournamentId), tournamentData);

      // 2️⃣ Archive participants with their results, then delete them
      const participantsSnap = await getDocs(collection(db, tournamentPath, 'participants'));
      const participantIds = [];
      
      // Archive each participant with their result AND update user's tournament record
      const participantOperations = participantsSnap.docs.map(async (docSnap) => {
        const userId = docSnap.id;
        participantIds.push(userId);
        const participantData = docSnap.data();
        
        // 🔥 Ensure result is not "on going" - replace with "Participated" if needed
        const finalResult = (participantData.result === 'on going' || !participantData.result) 
          ? 'Participated' 
          : participantData.result;
        
        // Archive to: tournament-v2/archived/list/{tournamentId}/participants/{userId}
        await setDoc(
          doc(db, 'tournament-v2', 'archived', 'list', selectedTournamentId, 'participants', userId),
          {
            ...participantData,
            result: finalResult,
            placement: finalResult
          }
        );
        
        // 🔥 Update user's tournament record with final result
        const userTourRef = doc(db, 'users', userId, 'tournaments', selectedTournamentId);
        const userTourSnap = await getDoc(userTourRef);
        if (userTourSnap.exists()) {
          await updateDoc(userTourRef, {
            result: finalResult,
            placement: finalResult
          });
        }
      });
      await Promise.all(participantOperations);
      
      // Now delete participants from active tournament
      const participantDeletes = participantsSnap.docs.map(docSnap => deleteDoc(docSnap.ref));
      await Promise.all(participantDeletes);

      // 3️⃣ Delete all TournamentSchedule
      const scheduleSnap = await getDocs(collection(db, tournamentPath, 'TournamentSchedule'));
      const scheduleDeletes = scheduleSnap.docs.map(docSnap => deleteDoc(docSnap.ref));
      await Promise.all(scheduleDeletes);

      // 4️⃣ Remove matches for this tournament in users/{userId}/matches
      const userMatchDeletes = [];
      for (const userId of participantIds) {
        const userMatchesSnap = await getDocs(collection(db, 'users', userId, 'matches'));
        userMatchesSnap.forEach(matchDoc => {
          if (matchDoc.data()?.tournamentId === selectedTournamentId) {
            userMatchDeletes.push(deleteDoc(matchDoc.ref));
          }
        });
      }
      await Promise.all(userMatchDeletes);

      // 5️⃣ Delete the tournament document itself
      await deleteDoc(tourRef);

      toast.success('✅ Tournament archived and fully removed from active list!');
      setSelectedTournamentId('');
      setTournamentInfo(null);
      setParticipants([]);
      setPairings([]);

    } catch (error) {
      // console.error(error);
      toast.error('❌ Failed to end tournament.');
    } finally {
      setEndingTournament(false);
    }
  };




  // UI
  return (
    <div className="admin-tournament-container">
      <div className="admin-section">

        <h2>⚙️ Select Tournament to Edit</h2>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', marginBottom: '20px' }}>
          <button onClick={handleCreateTournament} disabled={creatingTournament} className="create-tournament-button">
            <PlusIcon size={16} className="mr-2" />
            Create Tournament
          </button>

          {tournaments.map((t) => (
            <button
              key={t.id}
              onClick={() => setSelectedTournamentId(t.id)}
              className={selectedTournamentId === t.id ? 'tournament-button active' : 'tournament-button inactive'}
            >
              {t.title || t.id}
            </button>
          ))}
        </div>
      </div>

      {!selectedTournamentId && <p>Please select a tournament to edit.</p>}

      {selectedTournamentId && tournamentInfo && (
        <>
          <div className="admin-section">
            <h2>📋 Tournament Info</h2>

            <div className="edit-tournament-form">
              <div className="form-group">
                <label>Title</label>
                <input
                  className="text-admin"
                  type="text"
                  value={tournamentInfo.title || ''}
                  onChange={(e) => setTournamentInfo({ ...tournamentInfo, title: e.target.value })}
                />
              </div>

              <div className="form-group">
                <label>Date</label>
                <input
                  className="text-admin"
                  type="text"
                  value={tournamentInfo.date || ''}
                  onChange={(e) => setTournamentInfo({ ...tournamentInfo, date: e.target.value })}
                />
              </div>

              <div className="form-group">
                <label>Banner URL</label>
                <input
                  className="text-admin"
                  type="text"
                  value={tournamentInfo.bannerUrl || ''}
                  onChange={(e) => setTournamentInfo({ ...tournamentInfo, bannerUrl: e.target.value })}
                />
              </div>

              <div className="form-group">
                <label>Fees</label>
                <input
                  className="text-admin"
                  type="text"
                  value={tournamentInfo.fees || ''}
                  onChange={(e) => setTournamentInfo({ ...tournamentInfo, fees: e.target.value })}
                />
              </div>
              
              <div className="form-group">
                <label>DP Price (not shown to users)</label>
                <input
                  className="text-admin"
                  type="number"
                  min="0"
                  value={tournamentInfo.dpPrice || ''}
                  onChange={(e) => setTournamentInfo({ ...tournamentInfo, dpPrice: Number(e.target.value) })}
                />
              </div>

              <div className="form-group">
                <label>Format</label>
                <input
                  className="text-admin"
                  type="text"
                  value={tournamentInfo.format || ''}
                  onChange={(e) => setTournamentInfo({ ...tournamentInfo, format: e.target.value })}
                />
              </div>

              <div className="form-group">
                <label>Link</label>
                <input
                  className="text-admin"
                  type="text"
                  value={tournamentInfo.link || ''}
                  onChange={(e) => setTournamentInfo({ ...tournamentInfo, link: e.target.value })}
                />
              </div>

              <div className="form-group">
                <label>Platform</label>
                <input
                  className="text-admin"
                  type="text"
                  value={tournamentInfo.platform || ''}
                  onChange={(e) => setTournamentInfo({ ...tournamentInfo, platform: e.target.value })}
                />
              </div>

              <div className="form-group">
                <label>
                  Started
                  <input
                    type="checkbox"
                    checked={tournamentInfo.started || false}
                    onChange={(e) => setTournamentInfo({ ...tournamentInfo, started: e.target.checked })}
                    style={{ marginLeft: '8px' }}
                  />
                </label>
              </div>

              <div className="form-group allowed-division-editor">
                <h3>🎖️ Allowed Divisions</h3>
                {allDivisions.map((division) => (
                  <label key={division} className="division-checkbox">
                    <input
                      type="checkbox"
                      checked={tournamentInfo.allowedDivision?.[division] || false}
                      onChange={(e) =>
                        setTournamentInfo({
                          ...tournamentInfo,
                          allowedDivision: {
                            ...tournamentInfo.allowedDivision,
                            [division]: e.target.checked,
                          },
                        })
                      }
                    />
                    {division}
                  </label>
                ))}
              </div>


              <div className="form-group">
                <label>Description</label>
                <textarea
                  className="big-textarea"
                  value={tournamentInfo.description || ''}
                  onChange={(e) => setTournamentInfo({ ...tournamentInfo, description: e.target.value })}
                />
              </div>

              <button onClick={handleSaveTournamentInfo}>💾 Save Tournament Info</button>
            </div>
          </div>




          <div className="admin-section">
            <h2>👥 Participants</h2>
            <table>
              <thead>
                <tr>
                  <th>Pseudo</th>
                  <th>UID</th>
                  <th>Result</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {participants.map((p) => (
                  <tr key={p.id}>
                    <td>{p.pseudo}</td>
                    <td style={{ fontSize: '0.85em', color: '#888' }}>{p.id}</td>
                    <td>
                      <input
                        className="text-admin"
                        type="text"
                        value={p.result || ''}
                        onChange={(e) => handleResultChange(p.id, e.target.value)}
                      />
                    </td>
                    <td>
                      <button onClick={() => setRemoveModalParticipant(p)}>❌ Remove</button>

                    </td>
                    <td>
                      <button onClick={() => {
                        setManageResultUser(p);
                        setStatIncrements({ matches: 0, wins: 0, losses: 0, champion: 0 });
                      }}>🛠️ Manage Result</button>
                      <button 
                        onClick={() => {
                          console.log('🔍 Opening deck modal for user:', p.pseudo, 'in tournament:', selectedTournamentId);
                          fetchUserDeck(selectedTournamentId, p.id, p.pseudo);
                          setShowDeckModal(true);
                        }}
                        style={{ marginLeft: '5px' }}
                      >📋 View Deck</button>
                      {resultUpdateLog.some(r => r.id === p.id) && (
                        <span title={
                          (() => {
                            const entry = resultUpdateLog.find(r => r.id === p.id);
                            return `✔️ Updated: +${entry.matches}M / +${entry.wins}W / +${entry.losses}L / +${entry.champion}🏆`;
                          })()
                        } style={{ marginRight: '5px', marginLeft: '8px', color: 'green', fontSize: '1.2em', cursor: 'pointer' }}>✔️</span>
                      )}
                    </td>




                  </tr>
                ))}
              </tbody>
            </table>
            <div className="tournament-actions">
              <button onClick={handleSaveAll}>💾 Save All Results</button>
              <button onClick={handleCopyParticipants}>📋 Copy Participants</button>
            </div>

          </div>

          <div className="admin-section">
            <h2>🔗 Pairings</h2>
            <span className="pairing-label">Player A:</span>
            <select
              className="pairing-select"
              value={selectedPlayerA}
              onChange={(e) => setSelectedPlayerA(e.target.value)}
            >
              <option value="">-- Select Player A --</option>

              {participants.map((p) => (
                <option key={p.id} value={p.id}>{p.pseudo}</option>
              ))}
            </select>


            <span className="pairing-label">Player B:</span>
            <select
              className="pairing-select"
              value={selectedPlayerB}
              onChange={(e) => setSelectedPlayerB(e.target.value)}
            >
              <option value="">-- Select Player B --</option>

              {participants.map((p) => (
                <option key={p.id} value={p.id}>{p.pseudo}</option>
              ))}
            </select>

            <span className="pairing-label">Round:</span>
            <input
              type="text"
              className="pairing-input text-admin"
              value={pairingRound}
              onChange={(e) => setPairingRound(e.target.value)}
              placeholder="e.g., Round 1, Finals, etc."
            />

            <button onClick={handleAddPairing}><PlusIcon size={16} className="mr-2" />Add Pairing</button>

            <table>
              <thead>
                <tr>
                  <th>Player A</th>
                  <th>Player B</th>
                  <th>Status</th>
                  <th>Round</th>
                  <th>Admin Note</th>
                  <th>Player A Note</th>
                  <th>Player A Result</th>
                  <th>Player B Note</th>
                  <th>Player B Result</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {pairings
                  .filter((m) =>
                    !selectedRoundFilter || m.round === selectedRoundFilter
                  )
                  .map((m) => (
                    <tr key={m.id}>
                      <td>{getPseudoById(m.playerAId)}</td>
                      <td>{getPseudoById(m.playerBId)}</td>
                      <td>
                        <input
                          className="text-admin"
                          type="text"
                          value={m.status || ''}
                          onChange={(e) =>
                            setPairings((prev) =>
                              prev.map((p) =>
                                p.id === m.id ? { ...p, status: e.target.value } : p
                              )
                            )
                          }
                        />
                      </td>
                      <td>
                        <input
                          className="text-admin"
                          type="text"
                          value={m.round || ''}
                          onChange={(e) =>
                            setPairings((prev) =>
                              prev.map((p) =>
                                p.id === m.id ? { ...p, round: e.target.value } : p
                              )
                            )
                          }
                        />
                      </td>
                      <td>
                        <input
                          className="text-admin"
                          type="text"
                          value={m.adminNote || ''}
                          onChange={(e) =>
                            setPairings((prev) =>
                              prev.map((p) =>
                                p.id === m.id ? { ...p, adminNote: e.target.value } : p
                              )
                            )
                          }
                        />
                      </td>
                      <td>
                        <input
                          className="text-admin"
                          type="text"
                          value={m.playerANote || ''}
                          onChange={(e) =>
                            setPairings((prev) =>
                              prev.map((p) =>
                                p.id === m.id ? { ...p, playerANote: e.target.value } : p
                              )
                            )
                          }
                        />
                      </td>
                      <td>{m.reports && m.playerAId && m.reports[m.playerAId] ? m.reports[m.playerAId] : ''}</td>
                      <td>
                        <input
                          className="text-admin"
                          type="text"
                          value={m.playerBNote || ''}
                          onChange={(e) =>
                            setPairings((prev) =>
                              prev.map((p) =>
                                p.id === m.id ? { ...p, playerBNote: e.target.value } : p
                              )
                            )
                          }
                        />
                      </td>
                      <td>{m.reports && m.playerBId && m.reports[m.playerBId] ? m.reports[m.playerBId] : ''}</td>
                      <td>
                        <button onClick={() => handleSavePairing(m)} disabled={!!savingPairing[m.id]}>💾 Save</button>
                        <button onClick={() => handleDeletePairing(m.id)} disabled={!!deletingPairing[m.id]}>❌ Delete</button>
                        <button onClick={() => openDiscussion(m)} className="discussion-button">💬 Discussion</button>
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>

          </div>
        </>
      )}

      {/* Debug Info */}
      {selectedTournamentId && (
        <div style={{ marginTop: '2rem', padding: '15px', background: '#2a2a2a', borderRadius: '6px', fontSize: '0.85rem', color: '#888' }}>
          <strong>DEBUG:</strong> selectedTournamentId={selectedTournamentId}, pairings.length={pairings?.length || 0}
        </div>
      )}

      {removeModalParticipant && (
        <div className="modal-overlay" onClick={() => setRemoveModalParticipant(null)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <h3>Remove {removeModalParticipant.pseudo}?</h3>
            <p>Choose an option below:</p>
            <button onClick={() => handleRemoveParticipant(removeModalParticipant, false)} disabled={removingParticipant}>Remove from Tournament</button>
            <button onClick={() => handleRemoveParticipant(removeModalParticipant, true)} disabled={removingParticipant}>Remove from Everything</button>
            <button onClick={() => setRemoveModalParticipant(null)}>Cancel</button>
          </div>
        </div>
      )}
      {selectedTournamentId && tournamentInfo && (
        <div style={{ textAlign: 'center', margin: '2rem 0' }}>
          <button
            className="end-tournament-button"
            onClick={handleEndTournament}
            disabled={endingTournament}
          >
            🏁 END TOURNAMENT
          </button>
        </div>
      )}

      {manageResultUser && (
        <div className="modal-overlay" onClick={() => setManageResultUser(null)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <h3>Manage Result – {manageResultUser.pseudo}</h3>

            <div className="field-group">
              <label>+ Matches</label>
              <input
                className="text-admin"
                type="number"
                value={statIncrements.matches}
                onChange={(e) => setStatIncrements({ ...statIncrements, matches: Number(e.target.value) })}
              />
              <label>+ Wins</label>
              <input
                className="text-admin"
                type="number"
                value={statIncrements.wins}
                onChange={(e) => setStatIncrements({ ...statIncrements, wins: Number(e.target.value) })}
              />
              <label>+ Losses</label>
              <input
                className="text-admin"
                type="number"
                value={statIncrements.losses}
                onChange={(e) => setStatIncrements({ ...statIncrements, losses: Number(e.target.value) })}
              />
              <label>+ Champion 🏆</label>
              <input
                className="text-admin"
                type="number"
                value={statIncrements.champion}
                onChange={(e) => setStatIncrements({ ...statIncrements, champion: Number(e.target.value) })}
              />
            </div>

            <div className="modal-buttons">
              <button onClick={async () => {
                try {
                  const userRef = doc(db, "users", manageResultUser.id);
                  const userSnap = await getDoc(userRef);
                  const stats = userSnap.data()?.stats || {
                    matches: 0, wins: 0, losses: 0, champion: 0
                  };

                  // Import validation utilities
                  const { safeIncrementStats, calculateYP } = await import('../utils/statsValidation');
                  
                  const newStats = safeIncrementStats(stats, statIncrements);
                  const newYP = calculateYP(newStats.wins);

                  // Create backup before updating stats
                  const { updateUserStatsWithBackup } = await import('../utils/clientBackupWrapper');
                  
                  await updateUserStatsWithBackup(manageResultUser.id, {
                    stats: newStats,
                    YP: newYP
                  }, 'admin_stats_update', {
                    tournamentId: selectedTournamentId,
                    statIncrements: statIncrements,
                    previousStats: stats,
                    newStats: newStats
                  });

                  // ✅ Log it
                  setResultUpdateLog(prev => {
                    const updated = [
                      ...prev,
                      {
                        id: manageResultUser.id,
                        pseudo: manageResultUser.pseudo,
                        ...statIncrements
                      }
                    ];
                    localStorage.setItem('resultUpdateLog', JSON.stringify(updated));
                    return updated;
                  });


                  toast.success("✅ Stats updated!");
                  setManageResultUser(null);
                } catch (err) {
                  toast.error("❌ Failed to update stats.");
                }
              }}>Save</button>


              <button onClick={() => setManageResultUser(null)}>Cancel</button>
            </div>
          </div>

        </div>
      )}

      {/* Tournament Decks Modal */}
      {showDeckModal && (
        <div className="modal-overlay" onClick={() => setShowDeckModal(false)}>
          <div className="modal deck-modal" onClick={(e) => e.stopPropagation()}>
            <h3>User Deck - {tournamentDecks[0]?.username || 'Unknown User'}</h3>
            
            {loadingDecks ? (
              <div className="loading-spinner">Loading decks...</div>
            ) : tournamentDecks.length === 0 ? (
              <div className="no-decks">
                <p>This user has not submitted a deck for this tournament yet.</p>
              </div>
            ) : (
              <div className="decks-container">
                {tournamentDecks.map((deck, index) => (
                  <div key={index} className="deck-item">
                    <div className="deck-header">
                      <div className="deck-header-left">
                        <h4>{deck.username}</h4>
                        <span className="deck-name">{deck.deckName}</span>
                        <span className="deck-date">
                          Submitted: {deck.submittedAt?.toDate?.() ? 
                            deck.submittedAt.toDate().toLocaleDateString() : 
                            'Unknown date'
                          }
                        </span>
                      </div>
                      <button 
                        className="export-ydke-button"
                        onClick={() => {
                          const encodeYDKE = (main, extra, side) => {
                            const toBytes = (cards) => {
                              const bytes = [];
                              for (const card of cards) {
                                const id = Number(card.id);
                                if (!id || isNaN(id)) continue;
                                bytes.push(id & 0xff);
                                bytes.push((id >> 8) & 0xff);
                                bytes.push((id >> 16) & 0xff);
                                bytes.push((id >> 24) & 0xff);
                              }
                              return bytes;
                            };
                            
                            const toBase64 = (bytes) => {
                              let binary = '';
                              for (let i = 0; i < bytes.length; i++) {
                                binary += String.fromCharCode(bytes[i]);
                              }
                              return btoa(binary);
                            };
                            
                            const main64 = toBase64(toBytes(main));
                            const extra64 = toBase64(toBytes(extra));
                            const side64 = toBase64(toBytes(side));
                            const result = `ydke://${main64}!${extra64}!${side64}!`;
                            return result;
                          };
                          
                          const isValidCard = (c) => c?.id && !isNaN(Number(c.id)) && Number(c.id) > 1000;
                          const cleanedMain = deck.main.filter(isValidCard);
                          const cleanedExtra = deck.extra.filter(isValidCard);
                          const cleanedSide = deck.side.filter(isValidCard);
                          
                          const code = encodeYDKE(cleanedMain, cleanedExtra, cleanedSide);
                          navigator.clipboard.writeText(code);
                          toast.success('✅ YDKE code copied to clipboard!');
                        }}
                      >
                        📋 Export YDKE
                      </button>
                    </div>
                    
                    <div className="deck-content">
                      <div className="deck-section">
                        <h5>Main Deck ({deck.main.length})</h5>
                        <div className="card-list">
                          {deck.main.map((card, idx) => (
                            <div key={idx} className="card-display">
                              <img 
                                src={card.card_images?.[0]?.image_url || 'https://via.placeholder.com/80x115?text=Unknown'} 
                                alt={card.name || `Card ${card.id}`}
                                className="card-image"
                                title={card.name || `Card ${card.id}`}
                              />
                              <span className="card-name">{card.name || `Card ${card.id}`}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                      
                      <div className="deck-section">
                        <h5>Extra Deck ({deck.extra.length})</h5>
                        <div className="card-list">
                          {deck.extra.map((card, idx) => (
                            <div key={idx} className="card-display">
                              <img 
                                src={card.card_images?.[0]?.image_url || 'https://via.placeholder.com/80x115?text=Unknown'} 
                                alt={card.name || `Card ${card.id}`}
                                className="card-image"
                                title={card.name || `Card ${card.id}`}
                              />
                              <span className="card-name">{card.name || `Card ${card.id}`}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                      
                      <div className="deck-section">
                        <h5>Side Deck ({deck.side.length})</h5>
                        <div className="card-list">
                          {deck.side.map((card, idx) => (
                            <div key={idx} className="card-display">
                              <img 
                                src={card.card_images?.[0]?.image_url || 'https://via.placeholder.com/80x115?text=Unknown'} 
                                alt={card.name || `Card ${card.id}`}
                                className="card-image"
                                title={card.name || `Card ${card.id}`}
                              />
                              <span className="card-name">{card.name || `Card ${card.id}`}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
            
            <div className="modal-buttons">
              <button onClick={() => setShowDeckModal(false)}>Close</button>
            </div>
          </div>
        </div>
      )}

      {/* Discussion Modal */}
      {discussionModal && (
        <div className="modal-overlay" onClick={closeDiscussion}>
          <div className="discussion-modal" onClick={(e) => e.stopPropagation()}>
            <div className="discussion-header">
              <h3>💬 Discussion: {discussionModal.playerAName} vs {discussionModal.playerBName}</h3>
              <div className="discussion-header-actions">
                <button 
                  onClick={deleteAllMessages} 
                  className="delete-all-button"
                  disabled={deletingMessages || discussionMessages.length === 0}
                  title="Delete all messages"
                >
                  {deletingMessages ? 'Deleting...' : '🗑️ Delete All'}
                </button>
                <button onClick={closeDiscussion} className="close-button">✕</button>
              </div>
            </div>
            
            <div className="discussion-messages">
              {discussionMessages.length === 0 ? (
                <div className="no-messages">No messages yet. Start the conversation!</div>
              ) : (
                discussionMessages.map((message) => (
                  <div
                    key={message.id}
                    className={`discussion-message ${message.isAdminMessage ? 'admin-message' : 'player-message'}`}
                  >
                    <div className="message-header">
                      <strong>{message.senderName}</strong>
                      {message.isAdminMessage && <span className="admin-badge">ADMIN</span>}
                      <span className="message-time">{formatTimestamp(message.timestamp)}</span>
                    </div>
                    <div className="message-text">{message.text}</div>
                  </div>
                ))
              )}
            </div>
            
            <div className="discussion-input">
              <input
                type="text"
                value={discussionInput}
                onChange={(e) => setDiscussionInput(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && sendDiscussionMessage()}
                placeholder="Type your message as admin..."
                disabled={sendingMessage}
              />
              <button 
                onClick={sendDiscussionMessage} 
                disabled={!discussionInput.trim() || sendingMessage}
                className="send-button"
              >
                {sendingMessage ? 'Sending...' : 'Send'}
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default AdminTournamentPage;
